/**
 * Ablauf eines Matches von der Wartezeit bis zum Kampf
 * (Masterprompt Abschnitte 86, 87).
 *
 * Warum das eine eigene Regel ist:
 * Der Raum lief bisher vom ersten Augenblick an mit voller Simulation - auch
 * dann, wenn erst ein Spieler da war. Creeps spawnten, liefen ins Portal und
 * richteten Schaden an, lange bevor das Match ueberhaupt begonnen hatte. Wer
 * zuerst beitrat, war deshalb im Nachteil, und ein Match konnte entschieden
 * sein, bevor beide Spieler ueberhaupt gemeinsam gestartet waren.
 *
 * Jetzt gilt: Gekaempft wird erst, wenn beide Spieler da sind und Vorbereitung
 * und Countdown durch sind.
 *
 *   waiting -> lobby -> starting -> preparation -> countdown
 *           -> early ... overtime
 *
 * Der WARTERAUM kam am 05.10.2026 dazu (Produktentscheidung). Vorher sprang
 * das Match los, sobald zwei Leute im Raum waren - auch wenn einer davon
 * gerade gar nicht am Geraet sass. Jetzt startet es erst, wenn beide
 * ausdruecklich "Ready" gedrueckt haben.
 *
 * SEIT DEM 13.10.2026 GIBT ES EINE ZEITGRENZE - ABER NUR IM SCHNELLMATCH.
 *
 * Sind zwei da, laufen LOBBY_WAIT_SECONDS; danach geht es von selbst los.
 * In einer privaten Lobby bleibt es beim alten: Dort startet nur das
 * zweite Ready.
 *
 * Bis dahin galt die Zeitgrenze nirgends, und zwar aus einem guten Grund
 * (05.10.2026): Ein Automatikstart liess Matches gegen Leute beginnen, die
 * gar nicht am Geraet sassen. Eine Minute ist lang genug, dass dieser Fall
 * selten wird - und im Schnellmatch kennt man einander nicht und will
 * spielen, statt aufeinander zu warten.
 *
 * Faellt ein Spieler weg, geht es zurueck auf waiting und der Ablauf beginnt
 * von vorn. Ein bereits entschiedenes Match (finished) bleibt entschieden.
 */

import {
  COUNTDOWN_SECONDS,
  LOBBY_COUNTDOWN_SECONDS,
  PREPARATION_SECONDS,
} from '@dueline/game-data';
import type { MatchPhase } from '@dueline/shared';
import { getMatchPhase } from './match-time.js';

/** Anzahl Spieler, die ein Match braucht. */
const REQUIRED_PLAYERS = 2;

export interface MatchFlowState {
  phase: MatchPhase;
  /** Restzeit der laufenden Phase in Sekunden. Nur fuer Vorbereitung und Countdown. */
  phaseSecondsRemaining: number;
  /** Verstrichene KAMPFZEIT in Sekunden. Waechst erst ab dem Kampf. */
  elapsedSeconds: number;
}

export interface MatchFlowResult extends MatchFlowState {
  /**
   * Darf in diesem Schritt gekaempft werden?
   * Nur dann spawnen Creeps, wird Schaden verrechnet und faellt ein Portal.
   */
  simulate: boolean;
}

/** Wird in dieser Phase gekaempft? */
export function isBattlePhase(phase: MatchPhase): boolean {
  return (
    phase !== 'waiting' &&
    phase !== 'lobby' &&
    phase !== 'starting' &&
    phase !== 'preparation' &&
    phase !== 'countdown' &&
    phase !== 'finished'
  );
}

/** Sitzt man gerade im Warteraum (mit oder ohne laufenden Countdown)? */
export function isLobbyPhase(phase: MatchPhase): boolean {
  return phase === 'lobby' || phase === 'starting';
}

/**
 * Hat das Match noch gar nicht begonnen?
 *
 * Dasselbe wie isLobbyPhase, aber EINSCHLIESSLICH des Wartens auf den
 * zweiten Spieler - also der ganze Weg vom Beitreten bis zum Startschuss.
 *
 * Daran haengen die Regeln fuer das Weggehen (Produktentscheidung vom
 * 15.10.2026, Robin: "im Idealfall sollte die Lobby sofort wieder frei
 * werden, sobald in der Lobby jemand leaved - anders als ingame"):
 *
 *   - Der Platz wird SOFORT frei. Keine Schonfrist, kein reservierter
 *     Sitz. Im Match ist die Schonfrist richtig (ein Funkloch soll kein
 *     verlorenes Match bedeuten); hier stuende sie dem naechsten Gegner
 *     nur im Weg.
 *   - Es wird NICHT angehalten. Vorher blieb die Uhr der Lobby stehen,
 *     ohne dass jemand erfuhr, warum - genau der gemeldete Fehler.
 *   - Und es ist KEINE Aufgabe: Wer eine Lobby verlaesst, verliert
 *     nichts, und der andere gewinnt nichts. Er wartet einfach weiter,
 *     und fuer den naechsten faengt die Uhr von vorne an.
 */
export function istWarteraum(phase: MatchPhase): boolean {
  return phase === 'waiting' || isLobbyPhase(phase);
}

/**
 * Rechnet den Matchablauf einen Schritt weiter.
 *
 * @param state       Aktueller Stand.
 * @param playerCount Anzahl der verbundenen Spieler.
 * @param dt          Dauer des Schrittes in Sekunden.
 * @param allReady    Haben ALLE Spieler im Warteraum "Ready" gedrueckt?
 * @param lobbyWartezeit Sekunden, nach denen die Lobby von selbst startet.
 *   null heisst: gar nicht - dann startet nur das zweite Ready. So ist es in
 *   einer privaten Lobby (Produktentscheidung vom 13.10.2026).
 */
export function advanceMatchFlow(
  state: MatchFlowState,
  playerCount: number,
  dt: number,
  allReady = false,
  lobbyWartezeit: number | null = null,
): MatchFlowResult {
  // Ein entschiedenes Match bleibt entschieden.
  if (state.phase === 'finished') {
    return { ...state, phaseSecondsRemaining: 0, simulate: false };
  }

  // Fehlt ein Spieler, ruht alles. Die bereits verstrichene Kampfzeit bleibt
  // erhalten - sie ist Teil des laufenden Matches.
  if (playerCount < REQUIRED_PLAYERS) {
    return {
      phase: 'waiting',
      phaseSecondsRemaining: 0,
      elapsedSeconds: state.elapsedSeconds,
      simulate: false,
    };
  }

  /*
   * Beide da, aber noch nichts begonnen: ab in den Warteraum.
   *
   * Hier - und nur hier - wird die Uhr gestellt. Geht einer wieder, faellt
   * der Ablauf oben auf "waiting" zurueck; kommt der naechste, beginnt die
   * Minute von vorn. Genau so soll es sein: Der Verbliebene wartet weiter,
   * und der Neue bekommt seine volle Zeit zum Waehlen.
   */
  if (state.phase === 'waiting') {
    return {
      phase: 'lobby',
      phaseSecondsRemaining: lobbyWartezeit ?? 0,
      elapsedSeconds: state.elapsedSeconds,
      simulate: false,
    };
  }

  /*
   * Im Warteraum gibt es zwei Wege zum Countdown: Beide druecken Ready,
   * oder die Uhr laeuft ab. Ohne Uhr (private Lobby) bleibt nur der erste.
   */
  if (state.phase === 'lobby') {
    const rest = state.phaseSecondsRemaining - dt;
    const zeitUm = lobbyWartezeit !== null && rest <= 0;

    if (!allReady && !zeitUm) {
      return {
        ...state,
        phase: 'lobby',
        // Ohne Uhr steht die Anzeige still - sonst zaehlte sie ins Minus.
        phaseSecondsRemaining: lobbyWartezeit === null ? 0 : rest,
        simulate: false,
      };
    }

    return {
      phase: 'starting',
      phaseSecondsRemaining: LOBBY_COUNTDOWN_SECONDS,
      elapsedSeconds: state.elapsedSeconds,
      simulate: false,
    };
  }

  /*
   * Der Countdown laeuft durch - ein Ready laesst sich jetzt nicht mehr
   * zuruecknehmen. Wer doch nicht will, verlaesst den Raum; dann faellt
   * der Ablauf oben ueber playerCount ohnehin auf waiting zurueck.
   */
  if (state.phase === 'starting') {
    const rest = state.phaseSecondsRemaining - dt;
    if (rest > 0) {
      return { ...state, phaseSecondsRemaining: rest, simulate: false };
    }
    return {
      phase: 'preparation',
      // Ueberzeit mitnehmen, damit sich kein Rundungsfehler aufbaut.
      phaseSecondsRemaining: PREPARATION_SECONDS + rest,
      elapsedSeconds: state.elapsedSeconds,
      simulate: false,
    };
  }

  if (state.phase === 'preparation' || state.phase === 'countdown') {
    const rest = state.phaseSecondsRemaining - dt;
    if (rest > 0) {
      return { ...state, phaseSecondsRemaining: rest, simulate: false };
    }

    if (state.phase === 'preparation') {
      return {
        phase: 'countdown',
        // Die Ueberzeit wird mitgenommen, damit sich ueber viele Schritte
        // kein Rundungsfehler aufbaut.
        phaseSecondsRemaining: COUNTDOWN_SECONDS + rest,
        elapsedSeconds: state.elapsedSeconds,
        simulate: false,
      };
    }

    // Countdown vorbei: Ab jetzt wird gekaempft.
    return {
      phase: getMatchPhase(state.elapsedSeconds),
      phaseSecondsRemaining: 0,
      elapsedSeconds: state.elapsedSeconds,
      simulate: true,
    };
  }

  // Kampf laeuft: Die Matchzeit laeuft mit und bestimmt die Phase.
  const elapsedSeconds = state.elapsedSeconds + dt;
  return {
    phase: getMatchPhase(elapsedSeconds),
    phaseSecondsRemaining: 0,
    elapsedSeconds,
    simulate: true,
  };
}
